import { useState, useMemo, useCallback } from 'react';

type ColumnFilter = {
	id: string;
	value: string[] | string | number | boolean | unknown;
};

interface Props<T> {
	data: T[];
	// Optional external control for showFilterInputs
	showFilterInputs?: boolean;
	onShowFilterInputsChange?: (show: boolean) => void;
}

type UseColumnFilteringReturn = {
	columnFilters: ColumnFilter[];
	setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFilter[]>>;
	showFilterInputs: boolean;
	setShowFilterInputs: (show: boolean) => void;
	getUniqueColumnValues: (columnId: string) => string[];
	updateColumnFilter: (columnId: string, filterValue: unknown) => void;
	clearAllFilters: () => void;
};

export function useColumnFiltering<T extends Record<string, unknown>>({
	data,
	showFilterInputs: externalShowFilterInputs,
	onShowFilterInputsChange,
}: Props<T>): UseColumnFilteringReturn {
	const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);

	// Internal state for showFilterInputs (used when external control is not provided)
	const [internalShowFilterInputs, setInternalShowFilterInputs] = useState(false);

	// Use external state if provided, otherwise use internal state
	const showFilterInputs = externalShowFilterInputs ?? internalShowFilterInputs;

	// Use external setter if provided, otherwise use internal setter
	const setShowFilterInputs = useCallback(
		(show: boolean) => {
			if (onShowFilterInputsChange) {
				onShowFilterInputsChange(show);
			} else {
				setInternalShowFilterInputs(show);
			}
		},
		[onShowFilterInputsChange]
	);

	// Helper function to get unique values for a column
	const getUniqueColumnValues = useMemo(() => {
		return (columnId: string): string[] => {
			const values = data.map((row) => {
				const value = row[columnId as keyof T];
				return String(value ?? '');
			});
			return Array.from(new Set(values)).filter(Boolean).sort();
		};
	}, [data]);

	// Helper function to update column filter
	const updateColumnFilter = useCallback((columnId: string, filterValue: unknown) => {
		setColumnFilters((prev) => {
			const existingIndex = prev.findIndex((filter) => filter.id === columnId);

			// Remove filter if value is empty/null/undefined or empty array
			const shouldRemove =
				filterValue === null ||
				filterValue === undefined ||
				filterValue === '' ||
				(Array.isArray(filterValue) && filterValue.length === 0);

			if (shouldRemove) {
				return prev.filter((filter) => filter.id !== columnId);
			}

			if (existingIndex >= 0) {
				// Update existing filter
				const updated = [...prev];
				updated[existingIndex] = { id: columnId, value: filterValue };
				return updated;
			} else {
				// Add new filter
				return [...prev, { id: columnId, value: filterValue }];
			}
		});
	}, []);

	const clearAllFilters = useCallback(() => {
		setColumnFilters([]);
	}, []);

	return {
		columnFilters,
		setColumnFilters,
		showFilterInputs,
		setShowFilterInputs,
		getUniqueColumnValues,
		updateColumnFilter,
		clearAllFilters,
	};
}
